import { attackInterval, characterSize } from '../characters/stats';
import { copiesOpponent, copyHitPoints, effectiveHitPoints } from '../game/battle';
import { pieceById } from '../game/movement';
import type { BattleResult, GameContext, GameState } from '../game/types';
import { createRng } from '../rng/seededRandom';
import { arenaTiming } from './arenaDef';
import { BURN_SECONDS, burnRadius, stepBurn } from './burn';
import { slowFighter } from './curse';
import { attack, distance, fall, hit, stepProjectiles } from './combat';
import { hazardRate } from './hazard';
import { moveFighter } from './physics';
import { FLIGHT_HEIGHT, TileMap } from './tilemap';
import {
  ATTACKER,
  DEFENDER,
  fighterOf,
  fightersOf,
  type ArenaDef,
  type BattleState,
  type Fighter,
  type FighterId,
  type FighterInput,
  type TilesetDef,
} from './types';

/** Fixed simulation step, seconds. */
export const BATTLE_DT = 1 / 30;
/** Seconds the safe zone takes to shrink to its minimum. */
export const SUDDEN_DEATH_SHRINK = 40;
export const SUDDEN_DEATH_MIN_RADIUS = 2.5;
export const SUDDEN_DEATH_DPS = 2;
export const MAX_PITCH = (80 * Math.PI) / 180;

export interface BattleContext {
  game: GameContext;
  arena: ArenaDef;
  map: TileMap;
}

export function createBattleContext(
  game: GameContext,
  arena: ArenaDef,
  tileset: TilesetDef,
): BattleContext {
  return { game, arena, map: new TileMap(arena, tileset) };
}

/** Where the fighters in a two-seat duel stand; the format carries up to six (D-19). */
export const DUEL_SEATS: readonly FighterId[] = [ATTACKER, DEFENDER];

/** Sets up the duel for the game's pending battle: hit points from the board, spawns from the arena. */
export function createBattle(ctx: BattleContext, gameState: GameState, seed: number): BattleState {
  const pending = gameState.pendingBattle;
  if (gameState.phase !== 'battle' || !pending) throw new Error('No battle pending');
  const rng = createRng(seed);
  // `force` is optional on a character. A fighter without one is taken to be worth what its
  // opponents are worth, and if nobody in the duel has a force they are all worth 1. Either way
  // the verdict falls back to the plain share of hit points left, which is a rule a player can
  // follow from the health bars, rather than to a second scale invented for the occasion.
  const characters = [pending.attackerId, pending.defenderId].map(
    (id) => ctx.game.roster[pieceById(gameState, id)!.character]!,
  );
  const known = characters.map((c) => c.force).filter((f): f is number => f !== undefined);
  const standInForce = known.length > 0 ? known.reduce((a, b) => a + b, 0) / known.length : 1;
  const make = (side: FighterId, seat: number, pieceId: number): Fighter => {
    const piece = pieceById(gameState, pieceId)!;
    const character = ctx.game.roster[piece.character]!;
    const spawn = ctx.arena.spawns[seat] ?? ctx.arena.spawns[0]!;
    const hp = effectiveHitPoints(ctx.game, gameState, piece, pending.x, pending.y);
    const flies = character.movement.type === 'fly';
    const size = characterSize(character.tier, flies);
    return {
      id: side,
      pieceId,
      faction: piece.faction,
      character: piece.character,
      // Tile `y` is world `z`; a flier rides at its hover height, everything else on the plane.
      pos: { x: spawn[0] + 0.5, y: flies ? FLIGHT_HEIGHT : 0, z: spawn[1] + 0.5 },
      vel: { x: 0, y: 0, z: 0 },
      yaw: 0,
      pitch: 0,
      hp,
      boardDamage: piece.damage,
      boardStartingHp: hp,
      maxHp: hp,
      movement: character.movement.type,
      force: character.force ?? standInForce,
      attackType: character.attack.type,
      damageType: character.attack.damageType ?? 'physical',
      effectChance: character.attack.effectChance ?? 1,
      protection: character.combat?.protection ?? 0,
      resistances: { ...character.combat?.resistances },
      damage: character.attack.damage,
      range:
        character.attack.type === 'burn'
          ? burnRadius(size.height, character.attack.reach)
          : character.attack.type === 'aura'
            ? 3
            : character.attack.type === 'burst'
              ? 2.5
              : 1.7,
      attackInterval: attackInterval(character.attack),
      steerableProjectile: character.attack.type === 'projectile' && character.spellcaster === true,
      cooldown: 0,
      windup: 0,
      stun: 0,
      alive: true,
      halfWidth: size.halfWidth,
      height: size.height,
      speed: size.speed * (character.combat?.speed ?? 1),
      dashTime: 0,
      dashCooldown: 0,
      dashDir: null,
      // A ring starts the duel full, so the first thing a ghost does is fight rather than wait.
      burn: character.attack.type === 'burn' ? (character.attack.duration ?? BURN_SECONDS) : 0,
      burnCapacity:
        character.attack.type === 'burn' ? (character.attack.duration ?? BURN_SECONDS) : 0,
      burning: false,
      burnTick: 0,
    };
  };
  const attacker = make(ATTACKER, 0, pending.attackerId);
  const defender = make(DEFENDER, 1, pending.defenderId);
  // A shapeshifter becomes its opponent, body and weapon (D-56); two of them fight hand to hand.
  const copy = (f: Fighter, other: Fighter) => {
    const piece = pieceById(gameState, f.pieceId)!;
    const opponent = pieceById(gameState, other.pieceId)!;
    if (copiesOpponent(ctx.game, piece, opponent))
      copyFighter(
        f,
        other,
        copyHitPoints(ctx.game, gameState, piece, opponent, pending.x, pending.y),
        ctx.game.roster[piece.character]!.hitPoints /
          ctx.game.roster[opponent.character]!.hitPoints,
      );
    else if (f.attackType === 'mimic') f.attackType = 'melee';
  };
  copy(attacker, defender);
  copy(defender, attacker);
  // Curse slows whoever carries it, after the copying: a shapeshifter copies a weapon, not a curse.
  for (const f of [attacker, defender])
    if (pieceById(gameState, f.pieceId)!.cursed > 0) slowFighter(f);
  attacker.yaw = Math.atan2(defender.pos.x - attacker.pos.x, defender.pos.z - attacker.pos.z);
  defender.yaw = Math.atan2(attacker.pos.x - defender.pos.x, attacker.pos.z - defender.pos.z);
  return {
    arenaId: ctx.arena.id,
    time: 0,
    seats: [...DUEL_SEATS],
    defenderSeat: DEFENDER,
    fighters: { [ATTACKER]: attacker, [DEFENDER]: defender },
    projectiles: [],
    nextProjectileId: 1,
    safeRadius: arenaRadius(ctx.arena),
    safeCentre: { x: ctx.arena.width / 2, z: ctx.arena.height / 2 },
    suddenDeath: false,
    rng: rng.state(),
    result: null,
    events: [],
  };
}

/** Half the arena's diagonal: the safe zone before it starts to shrink. */
function arenaRadius(arena: ArenaDef): number {
  return Math.hypot(arena.width, arena.height) / 2;
}

/**
 * A shapeshifter's copy of its opponent (D-56): the opponent's whole fighter, its size, way of
 * moving, speed, armour, resistances, weapon and worth for the timeout verdict, at the hit points
 * `copyHitPoints` gives it. `bodyRatio` is the shapeshifter's own body over the copied one: what
 * the copy loses comes back to the shapeshifter's own body at the same share.
 */
function copyFighter(f: Fighter, other: Fighter, hp: number, bodyRatio: number): void {
  f.form = other.character;
  f.hp = hp;
  f.maxHp = hp;
  f.boardStartingHp = hp * bodyRatio;
  f.movement = other.movement;
  f.pos = { ...f.pos, y: other.pos.y };
  f.halfWidth = other.halfWidth;
  f.height = other.height;
  f.speed = other.speed;
  f.force = other.force;
  f.protection = other.protection;
  f.resistances = { ...other.resistances };
  f.attackType = other.attackType;
  f.damageType = other.damageType;
  f.effectChance = other.effectChance;
  f.damage = other.damage;
  f.range = other.range;
  f.attackInterval = other.attackInterval;
  f.steerableProjectile = other.steerableProjectile;
  f.burnCapacity = other.burnCapacity;
  f.burn = other.burn;
}

/** One fixed step; mutates and returns the state. Inputs default to standing still. */
export function stepBattle(
  ctx: BattleContext,
  state: BattleState,
  inputs: Partial<Record<FighterId, FighterInput>>,
  dt = BATTLE_DT,
): BattleState {
  if (state.result) return state;
  state.events = [];
  state.time += dt;

  for (const f of fightersOf(state)) {
    if (!f.alive) continue;
    const input = inputs[f.id] ?? {
      moveX: 0,
      moveZ: 0,
      yaw: f.yaw,
      pitch: f.pitch,
      dash: false,
      attack: false,
    };
    // Facing, movement and attacking must agree on whether stun has expired.
    // Otherwise the recovery tick can release a shot in the previous direction.
    f.cooldown = Math.max(0, f.cooldown - dt);
    f.stun = Math.max(0, f.stun - dt);
    if (f.stun <= 0) {
      f.yaw = input.yaw;
      f.pitch = Math.max(-MAX_PITCH, Math.min(MAX_PITCH, input.pitch));
    }
    moveFighter(ctx.map, f, input, dt, fightersOf(state));
  }
  // Resolve attacks against one post-movement snapshot. A hit this tick must not
  // cancel another fighter's already-ready attack merely because its seat is later.
  state.pendingHits = [];
  for (const f of fightersOf(state)) {
    if (!f.alive) continue;
    const input = inputs[f.id] ?? {
      moveX: 0,
      moveZ: 0,
      yaw: f.yaw,
      pitch: f.pitch,
      dash: false,
      attack: false,
    };
    attack(state, ctx.map, f, input, dt);
    stepBurn(state, ctx.map, f, input, dt);
    // Hazards are a tileset field (`damagePerSecond`) rather than a hard-coded lava check, and
    // burn walkers only, less by their resistance (D-43, `hazard.ts`). The rate is the tile's
    // own: rounding each step's share up made 1 a second cost 3.
    const hazard = hazardRate(ctx.map.tileAt(f.pos.x, f.pos.z), f);
    if (hazard > 0) hit(state, f, hazard * dt, f.id, 'hazard');
  }
  stepProjectiles(state, ctx.map, dt, inputs);
  const pendingHits = state.pendingHits;
  delete state.pendingHits;
  for (const pending of pendingHits)
    hit(state, fighterOf(state, pending.target), pending.damage, pending.by, pending.source);

  // Sudden death: the safe zone shrinks and burns whoever stays outside it.
  const { timeout, suddenDeathAt } = arenaTiming(ctx.arena);
  if (state.time >= suddenDeathAt) {
    if (!state.suddenDeath) {
      state.suddenDeath = true;
      state.events.push({ type: 'sudden-death' });
      // Close in on where the fight actually is. On a `fit` arena that is within a metre or two
      // of the middle; on a 250-tile `scroll` map the middle can be nowhere near either fighter.
      const alive = fightersOf(state).filter((f) => f.alive);
      if (alive.length > 0) {
        state.safeCentre = {
          x: alive.reduce((sum, f) => sum + f.pos.x, 0) / alive.length,
          z: alive.reduce((sum, f) => sum + f.pos.z, 0) / alive.length,
        };
      }
    }
    const full = arenaRadius(ctx.arena);
    const k = Math.min(1, (state.time - suddenDeathAt) / SUDDEN_DEATH_SHRINK);
    state.safeRadius = full + (SUDDEN_DEATH_MIN_RADIUS - full) * k;
    for (const f of fightersOf(state)) {
      const { x, z } = state.safeCentre;
      if (f.alive && Math.hypot(f.pos.x - x, f.pos.z - z) > state.safeRadius)
        hit(state, f, SUDDEN_DEATH_DPS * dt, f.id, 'hazard');
    }
  }

  // A duel is over when one fighter is left standing, or none is. With two seats that is the
  // first death either way; with the six the format allows, it is the last one but one.
  if (fightersOf(state).filter((f) => f.alive).length <= 1) {
    state.result = resultFor(state);
    return state;
  }
  // The ring forces contact; the timeout is the backstop for what it cannot fix -- a fighter
  // walled in by a bad map, or one running away to prolong a game everyone else is waiting on.
  if (state.time >= timeout) {
    const winner = fittest(state);
    state.result = winner
      ? { winnerId: winner.pieceId, winnerDamage: carriedDamage(winner) }
      : { winnerId: null, winnerDamage: 0 };
    state.events.push({ type: 'timeout', winner: winner?.id ?? null });
  }
  return state;
}

/**
 * A fighter blows itself up (D-18). It is a forfeit: the blast kills only the one who commits it,
 * and loses them the square. It exists so a player sealed in by a bad map, or one who has decided
 * the fight is lost, can end it without waiting for the ring or the clock. Lives in the core so a
 * server and every client settle it identically.
 */
export function forfeitBattle(state: BattleState, fighter: FighterId): BattleState {
  if (state.result) return state;
  const me = fighterOf(state, fighter);
  const at = { ...me.pos };
  me.hp = 0;
  state.events = [{ type: 'suicide', fighter, at }];
  fall(state, me);
  // Over only when one fighter is left, or none, as a death settles it: with three or more in the
  // fight, one giving up used to end it for all of them, as if every one had fallen
  // (core-net-balance:bugs#3).
  if (fightersOf(state).filter((f) => f.alive).length <= 1) state.result = resultFor(state);
  return state;
}

/** What a fighter is worth at the timeout: its force, scaled by how much of itself is left. */
export function fitness(f: Fighter): number {
  return f.force * (f.hp / f.maxHp);
}

/**
 * The fighter the timeout verdict gives the square to, or null when nobody is left. On an exact
 * tie the defender keeps it, matching the board's rule that an attacker has to win to take it.
 */
function fittest(state: BattleState): Fighter | null {
  const alive = fightersOf(state).filter((f) => f.alive);
  if (alive.length === 0) return null;
  const best = Math.max(...alive.map(fitness));
  const tied = alive.filter((f) => fitness(f) === best);
  return tied.find((f) => f.id === state.defenderSeat) ?? tied[0]!;
}

function resultFor(state: BattleState): BattleResult {
  const alive = fightersOf(state).filter((f) => f.alive);
  if (alive.length !== 1) return { winnerId: null, winnerDamage: 0 };
  const winner = alive[0]!;
  return { winnerId: winner.pieceId, winnerDamage: carriedDamage(winner) };
}

/** Preserve existing wounds and convert difficulty-scaled damage back to board health units. */
function carriedDamage(winner: Fighter): number {
  const scale = (winner.boardStartingHp ?? winner.maxHp) / winner.maxHp;
  return (winner.boardDamage ?? 0) + Math.round(Math.max(0, winner.maxHp - winner.hp) * scale);
}

export { distance };
